import { ApolloError } from "@apollo/client";
import { HasuraRole } from "src/types/hasuraRole";
import {
  GET_PROFILE_BY_USER_ID_AND_ORG_ID,
  GET_PROFILE_FOR_AVELA_ADMIN,
} from "../scenes/parent/profile/graphql/queries";
import * as GQL from "../types/graphql";
import * as RemoteData from "../types/remoteData";
import { useOrganization } from "./useOrganization";
import { useRemoteDataQuery } from "./useRemoteDataQuery";
import useUser from "./useUser";

export type UserProfileError =
  | ServerError
  | UnauthenticatedError
  | ProfileNotFoundError;
class ServerError {
  readonly kind = "ServerError";
  constructor(public error: ApolloError) {}
}
class UnauthenticatedError {
  readonly kind = "UnauthenticatedError";
}
class ProfileNotFoundError {
  readonly kind = "ProfileNotFoundError";
}

export function useUserProfile(): RemoteData.RemoteData<
  UserProfileError,
  GQL.ProfileFragment
> {
  const user = useUser();
  const organization = useOrganization();
  const userId = user.status === "ok" ? user.data.id : null;
  const isAvelaAdmin =
    user.status === "ok" ? user.data.role === HasuraRole.ADMIN : false;
  const { remoteData } = useRemoteDataQuery<
    GQL.GetProfileByUserIdAndOrgId,
    GQL.GetProfileByUserIdAndOrgIdVariables
  >(GET_PROFILE_BY_USER_ID_AND_ORG_ID, {
    variables: {
      user_id: userId ?? "",
      organizationId: organization.map((org) => org.id).withDefault(""),
    },
    skip: userId === null || !organization.hasData() || isAvelaAdmin,
  });

  /* We need separate query for avela admin because organizationId will be empty when we're on avela admin dashboard. */
  const { remoteData: avelaAdminRemoteData } = useRemoteDataQuery<
    GQL.GetProfileForAvelaAdmin,
    GQL.GetProfileForAvelaAdminVariables
  >(GET_PROFILE_FOR_AVELA_ADMIN, {
    variables: {
      user_id: userId ?? "",
    },
    skip: userId === null || !isAvelaAdmin,
  });

  if (user.status === "loading") return RemoteData.loading();
  if (user.status === "unauthenticated")
    return RemoteData.failure(new UnauthenticatedError());

  return (isAvelaAdmin ? avelaAdminRemoteData : remoteData)
    .mapError<UserProfileError>((error) => new ServerError(error))
    .andThen((data) => {
      const profile = data.person[0];
      if (profile === undefined)
        return RemoteData.failure(new ProfileNotFoundError());

      return RemoteData.success(profile);
    });
}
